import { computed, onMounted, ref } from "vue"
import GoggleMapsWithMarkers from "../../../../Frontend/src/components/GoogleMapsWithMarkers/GoogleMapsWithMarkers.vue"
import MultiSelectDropdown from "../../../../Frontend/src/components/MultiSelectDropdown/MultiSelectDropdown.vue"
import debounce from "../../../../Frontend/src/foundation/js/debounce"

import zoomMapToCenters from "./methods/zoomMapToCenters"
import setFocusPoints from "./methods/setFocusPoints"
import initGooglePlacesAutocomplete from "./methods/initGooglePlacesAutocomplete"
import mapCategoriesToMultiSelectOptions from "./methods/mapCategoriesToMultiSelectOptions"
import getDistanceFromLatLonInKm from "./methods/getDistanceFromLatLonInKm"

export default {
  name: "CenterMap",
  props: {
    categories: {
      type: Array,
    },
    labels: {
      type: Array,
    },
    mapkey: {
      type: String,
      required: true,
    },
    centerInformation: {
      type: Array,
      required: true,
    },
    bankTypes: {
      type: Array,
      required: true,
    },
    designSchema: {
      type: String,
      default: 'nykredit',
    },
    searchRadius: {
      type: Number,
      default: 5,
    }
  },
  setup(props) {
    const centerInformationComplete = ref(props.centerInformation)
    const centerInformationFiltered = ref(props.centerInformation) // map will display these locations
    const googlePlacesSearchField = ref(null)
    const placeSelected = ref(null)
    const placeName = ref("")
    const focusPoints = ref([]) // map will zoom to these focus points
    const placeSearchQuery = ref("")
    const showMapsMobile = ref(true)
    const showListMobileCount = ref(3)
    const currentZoom = ref(7)

    // Map viewport state — updated on every map move, used only for URL writing
    const mapStartLat = ref(null)
    const mapStartLng = ref(null)
    const mapStartZoom = ref(null)

    // Initial viewport passed once to GoggleMapsWithMarkers — set from URL params on mount,
    // never changed afterwards so the map component does not re-render on every pan/zoom
    const initialViewportLat = ref(null)
    const initialViewportLng = ref(null)
    const initialViewportZoom = ref(null)

    const googlePlacesSearchResultsShown = ref(false)
    const googlePlacesSearchResultsRef = ref(null)

    const multiSelectCategoriesOptions = ref(mapCategoriesToMultiSelectOptions(props.categories))
    // Local copy of bankTypes so we can set initial selected state (e.g. when restoring from URL params)
    const localBankTypes = ref(props.bankTypes.map((bt) => ({ ...bt })))
    // Derive initial selection from localBankTypes so any server-side pre-selected banks are respected
    const selectedBanks = ref(localBankTypes.value.filter((bt) => bt.selected).map((bt) => bt.value))
    const selectedCategories = ref([])

    // --- URL params ---

    const updateUrlParams = () => {
      const params = new URLSearchParams(window.location.search)

      if (placeSelected.value?.geometry) {
        // Always write the place's own coords (not the live map center) so proximity
        // filtering is correct when the link is shared after the user has panned away.
        const placeLat = placeSelected.value.geometry.location.lat()
        const placeLng = placeSelected.value.geometry.location.lng()
        params.set('lat', placeLat)
        params.set('lng', placeLng)
        params.set('q', placeName.value)
        params.delete('zoom')
      } else {
        params.delete('q')
        const centerLat = mapStartLat.value
        const centerLng = mapStartLng.value
        if (centerLat != null && centerLng != null) {
          params.set('lat', centerLat)
          params.set('lng', centerLng)
        } else {
          params.delete('lat')
          params.delete('lng')
        }
        if (mapStartZoom.value !== null) {
          params.set('zoom', mapStartZoom.value)
        } else {
          params.delete('zoom')
        }
      }

      if (selectedCategories.value.length > 0) {
        params.set('categories', selectedCategories.value.join(','))
      } else {
        params.delete('categories')
      }

      if (selectedBanks.value.length > 0) {
        params.set('banks', selectedBanks.value.join(','))
      } else {
        params.delete('banks')
      }

      const queryString = params.toString()
      const hash = window.location.hash
      const newUrl = queryString
        ? `${window.location.pathname}?${queryString}${hash}`
        : `${window.location.pathname}${hash}`
      history.replaceState(null, '', newUrl)
    }

    const initFromUrlParams = () => {
      const params = new URLSearchParams(window.location.search)

      const lat = params.get('lat')
      const lng = params.get('lng')
      const place = params.get('q')
      const categoriesParam = params.get('categories')
      const banksParam = params.get('banks')
      const zoomParam = params.get('zoom')

      if (lat && lng) {
        const parsedLat = parseFloat(lat)
        const parsedLng = parseFloat(lng)
        const validCoords = Number.isFinite(parsedLat) && Number.isFinite(parsedLng)
          && parsedLat >= -90 && parsedLat <= 90
          && parsedLng >= -180 && parsedLng <= 180

        if (validCoords) {
          if (!place) {
            // No place query: restore the stored map viewport directly.
            mapStartLat.value = parsedLat
            mapStartLng.value = parsedLng
            initialViewportLat.value = parsedLat
            initialViewportLng.value = parsedLng
          } else {
            // Place query present: reconstruct placeSelected for proximity filtering.
            // q takes priority for the viewport — updateBounds will auto-fit to results.
            if (googlePlacesSearchField.value) {
              googlePlacesSearchField.value.value = place
            }
            placeSelected.value = {
              geometry: {
                location: {
                  lat: () => parsedLat,
                  lng: () => parsedLng,
                },
              },
            }
            placeName.value = place
          }
        }
      }

      if (categoriesParam) {
        const ids = categoriesParam.split(',').map(Number).filter(Boolean)
        selectedCategories.value = ids
        multiSelectCategoriesOptions.value.forEach((opt) => {
          opt.selected = ids.includes(parseInt(opt.value, 10))
        })
      }

      if (banksParam) {
        const banks = banksParam.split(',').filter(Boolean)
        selectedBanks.value = banks
        localBankTypes.value.forEach((bt) => {
          bt.selected = banks.includes(bt.value)
        })
      }

      if (zoomParam) {
        const parsedZoom = parseInt(zoomParam, 10)
        if (Number.isFinite(parsedZoom)) {
          const clampedZoom = Math.min(Math.max(parsedZoom, 0), 21)
          mapStartZoom.value = clampedZoom
          if (!place) {
            initialViewportZoom.value = clampedZoom
          }
        }
      }

      if (place || categoriesParam || banksParam) {
        applyAllFilters()
      }
    }

    // handling google places search results, to handle WCAG
    const googlePlacesSearchResultsMutationHandler = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes') {
          googlePlacesSearchResultsShown.value = googlePlacesSearchResultsRef.value.style.display !== 'none'

          if (mutation.attributeName === 'class' && mutation.target.classList.value.includes('pac-item-selected')) {
            mutation.target.setAttribute('aria-selected', 'true')
          }
        }

        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((item) => {
            item.setAttribute('aria-selected', 'false')
            observer.observe(item, { attributes: true })
          })
        }
      }
    }

    const observer = new MutationObserver(googlePlacesSearchResultsMutationHandler)

    const findGooglePlacesSearchResultsElement = () => {
      const searchResults = document.querySelector('.pac-container')
      if (!searchResults) {
        setTimeout(findGooglePlacesSearchResultsElement, 100)
        return
      }
      googlePlacesSearchResultsRef.value = searchResults
      googlePlacesSearchResultsRef.value.setAttribute('role', 'listbox')
      googlePlacesSearchResultsRef.value.setAttribute('id', 'googlePlacesSearchResults')
      observer.observe(googlePlacesSearchResultsRef.value, { attributes: true, childList: true })
    }

    onMounted(async () => {
      initGooglePlacesAutocomplete(googlePlacesSearchField.value, filterCentersAndZoom)
      findGooglePlacesSearchResultsElement()
      initFromUrlParams()
    })

    const showFilteredResults = computed(() => {
      return !!placeSelected.value?.geometry && centerInformationFiltered.value.length > 0
    })

    // controlling focus points and pins data

    const getFilteredByCategories = function (centers) {
      if (selectedCategories.value.length === 0) {
        return centers
      }
      return centers.filter((center) => {
        return center.categories.some((category) => selectedCategories.value.includes(category))
      })
    }

    const getFilteredByBanks = function (centers) {
      if (selectedBanks.value.length === 0) {
        return centers
      }
      return centers.filter((center) => {
        if (selectedBanks.value.includes('nykredit') && !center.isSparNord) return true
        if (selectedBanks.value.includes('sparnord') && center.isSparNord) return true
        return false
      })
    }

    const applyAllFilters = function () {
      // Step 1: Apply category and bank filters
      let filtered = centerInformationComplete.value
      filtered = getFilteredByCategories(filtered)
      filtered = getFilteredByBanks(filtered)

      // Step 2: Apply search proximity filter if a place is selected
      if (placeSelected.value && placeSelected.value.geometry) {
        filtered = zoomMapToCenters(placeSelected.value, filtered, props.searchRadius)
      }

      // Step 3: Update the displayed centers and focus points
      centerInformationFiltered.value = filtered

      if (centerInformationFiltered.value.length) {
        focusPoints.value = setFocusPoints(centerInformationFiltered.value)
      } else {
        focusPoints.value = []
      }
    }

    const filterCentersAndZoom = function (places) {
      showListMobileCount.value = 3
      const place = places.getPlace()
      const searchHasHits = !!place.geometry
      if (searchHasHits) {
        // Extract coords before Vue wraps the Place object in a Proxy — calling
        // lat()/lng() through a Vue Proxy breaks the internal LatLng methods.
        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        placeSelected.value = {
          geometry: {
            location: {
              lat: () => lat,
              lng: () => lng,
            },
          },
        }
        placeName.value = place.name ?? place.formatted_address ?? googlePlacesSearchField.value?.value ?? ''
        applyAllFilters()
      } else {
        placeSelected.value = null
        placeName.value = ''
      }
      updateUrlParams()
    }

    const resetCenterSearch = function () {
      googlePlacesSearchField.value.value = ""
      placeSelected.value = null
      placeName.value = ""
      applyAllFilters()
      showListMobileCount.value = 3
      updateUrlParams()
    }

    const handleSearchInput = function (event) {
      if (event.target.value === '' && placeSelected.value !== null) {
        placeSelected.value = null
        placeName.value = ""
        applyAllFilters()
        showListMobileCount.value = 3
        updateUrlParams()
      }
    }

    const filterCategories = function (userSelection) {
      showListMobileCount.value = 3
      const selectedCategoryIds = userSelection.filter((option) => option.selected).map((option) => parseInt(option.value))
      selectedCategories.value = selectedCategoryIds
      applyAllFilters()
      updateUrlParams()
    }

    const filterBanks = function (userSelection) {
      showListMobileCount.value = 3
      selectedBanks.value = userSelection.filter((option) => option.selected).map((option) => option.value)
      applyAllFilters()
      updateUrlParams()
    }

    const getDistance = (info) => {
      if (!placeSelected.value) return
      const distance = getDistanceFromLatLonInKm(placeSelected.value, info)
      return `${distance.toFixed(2)} km`
    }

    const handleShowMapMobile = () => {
      showMapsMobile.value = true
    }

    const handleShowListResultsMobile = () => {
      showMapsMobile.value = false
    }

    const handleShowAllListMobile = () => {
      showListMobileCount.value = 10000
    }

    const updateCurrentZoom = (zoom) => {
      currentZoom.value = zoom
    }

    const writeMapPositionToUrl = debounce(() => {
      updateUrlParams()
    }, 500)

    const handleMapMoved = ({ lat, lng, zoom }) => {
      mapStartLat.value = lat
      mapStartLng.value = lng
      mapStartZoom.value = zoom
      writeMapPositionToUrl()
    }

    return {
      centerInformationFiltered,
      filterCategories,
      filterBanks,
      focusPoints,
      googlePlacesSearchField,
      initialViewportLat,
      initialViewportLng,
      initialViewportZoom,
      localBankTypes,
      multiSelectCategoriesOptions,
      placeSearchQuery,
      showFilteredResults,
      resetCenterSearch,
      handleSearchInput,
      getDistance,
      showMapsMobile,
      handleShowMapMobile,
      handleShowListResultsMobile,
      showListMobileCount,
      handleShowAllListMobile,
      googlePlacesSearchResultsShown,
      handleMapMoved,
      updateCurrentZoom,
      currentZoom
    }
  },
  components: {
    GoggleMapsWithMarkers,
    MultiSelectDropdown,
  },
}
